import React, { useEffect, useRef, useState } from "react";
import {
    ChevronDown,
    ChevronRight,
    Pin,
    Square,
    Ruler,
    MoreVertical,
    Trash2,
    PenTool,
    Lightbulb,
} from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const TAKEOFF_META = {
    area: { Icon: Square, tone: "text-emerald-400", label: "Area" },
    linear: { Icon: Ruler, tone: "text-[#8fa4f0]", label: "Linear" },
    count: { Icon: MoreVertical, tone: "text-rose-400", label: "Count" },
};

// Split a quantity into (number, unit) so the sidebar can mimic Planswift's
// right-aligned "14.0  CU M" layout — big blue number, small grey unit.
function splitTakeoffQty(it) {
    if (it.type === "area" && it.quantity_sf != null) {
        return [Math.round(it.quantity_sf).toLocaleString(), "SF"];
    }
    if (it.type === "linear" && it.quantity_lf != null) {
        return [Math.round(it.quantity_lf).toLocaleString(), "LF"];
    }
    if (it.type === "count" && it.quantity_each != null) {
        return [String(it.quantity_each), "EA"];
    }
    return ["—", ""];
}

/**
 * A single document row in the workspace sidebar file list. Handles
 * drag-to-reorder (via dnd-kit), expand/collapse to show child pages,
 * rename inline, pin/unpin, split, and delete.
 *
 * As of iter-39, also renders its AI-takeoff shapes inline beneath the
 * row (Planswift-style) instead of pushing them into a separate bottom
 * panel that hid the document list.
 */
export default function SortableDocRow({
    file,
    active,
    canExpand,
    expanded,
    pageCount,
    isPdfFile,
    isSplitParent = false,
    activePage,
    activeFileId,
    childRows = [],
    needsSplit = false,
    splitting = false,
    takeoffItems = [],
    selectedTakeoffId = null,
    onSelectTakeoff,
    onJumpToTakeoff,
    onDeleteTakeoff,
    onToggleTakeoffVisibility,
    onSplit,
    onPickFile,
    onPickPage,
    onToggleExpand,
    onTogglePin,
    onDelete,
    onRename,
    renamingFileId = null,
    onRenameRequest,
}) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: file.id });

    const [renamingChildId, setRenamingChildId] = useState(null);
    const renameInputRef = useRef(null);
    // Parent row rename is driven externally by the DocsToolbar (iter-31).
    // Internal setter still exists for the double-click-on-name affordance
    // and blur-to-exit.
    const renamingSelf = renamingFileId === file.id;
    const setRenamingSelf = (v) => {
        if (!onRenameRequest) return;
        onRenameRequest(v ? file.id : null);
    };

    useEffect(() => {
        if ((renamingSelf || renamingChildId) && renameInputRef.current) {
            renameInputRef.current.focus();
            renameInputRef.current.select();
        }
    }, [renamingSelf, renamingChildId]);

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        zIndex: isDragging ? 10 : "auto",
    };

    const hasChildren = childRows.length > 0;
    // Iter-30 UX rule: once a PDF has been split into children, the PARENT
    // becomes a read-only container. The user edits the children instead.
    // Single-page parents + unsplit parents remain fully editable.
    const isLockedSplitParent = hasChildren;

    return (
        <li
            ref={setNodeRef}
            style={style}
            className={`group/item px-1 ${isDragging ? "shadow-lg shadow-black/40" : ""}`}
            data-pinned={file.pinned ? "true" : "false"}
        >
            <div className="flex items-stretch gap-1">
                {canExpand ? (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleExpand();
                        }}
                        data-testid={`workspace-toggle-pages-${file.id}`}
                        aria-label={expanded ? "Collapse pages" : "Expand pages"}
                        className="shrink-0 w-5 rounded-sm flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                    >
                        {expanded ? (
                            <ChevronDown className="w-3.5 h-3.5" />
                        ) : (
                            <ChevronRight className="w-3.5 h-3.5" />
                        )}
                    </button>
                ) : (
                    <div className="shrink-0 w-5" />
                )}
                {/* File-name button is ALSO the drag handle — press & hold to
                    reorder. Removed the separate grip icon + per-row
                    rename/pin/split/delete actions to maximize filename
                    width; those actions live in the panel toolbar now and
                    apply to whichever file is currently selected. */}
                <button
                    type="button"
                    onClick={() => {
                        if (renamingSelf) return;
                        // Split parents are un-clickable for viewing —
                        // clicking expands/collapses the children list
                        // instead. The user navigates into actual pages
                        // via the child rows or Prev/Next buttons.
                        if (isSplitParent) {
                            onToggleExpand && onToggleExpand();
                            return;
                        }
                        onPickFile();
                    }}
                    data-testid={`workspace-page-${file.id}`}
                    aria-disabled={isSplitParent ? "true" : "false"}
                    title={
                        isSplitParent
                            ? "This PDF has been split into pages — open a page below."
                            : undefined
                    }
                    {...attributes}
                    {...(renamingSelf ? {} : listeners)}
                    className={`flex-1 min-w-0 flex items-center gap-2 px-2 py-2 text-sm text-left rounded-sm transition-colors touch-none cursor-grab active:cursor-grabbing ${
                        active
                            ? "bg-[#1a3fd1]/15 text-white border-l-2 border-[#1a3fd1]/50"
                            : isSplitParent
                              ? "text-slate-400 hover:bg-white/10"
                              : "text-slate-300 hover:bg-white/10 hover:text-white"
                    }`}
                >
                    <span
                        className={`w-4 h-4 shrink-0 rounded-sm border ${
                            active ? "bg-[#1a3fd1] border-[#1a3fd1]/50" : "border-slate-500"
                        }`}
                    />
                    {renamingSelf ? (
                        <input
                            ref={renameInputRef}
                            type="text"
                            defaultValue={file.display_name || file.original_filename}
                            placeholder={file.original_filename}
                            data-testid={`workspace-rename-input-${file.id}`}
                            onClick={(e) => e.stopPropagation()}
                            onBlur={(e) => {
                                const v = e.target.value;
                                setRenamingSelf(false);
                                if ((v || "").trim() !== (file.display_name || "").trim()) {
                                    onRename && onRename(file, v);
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    e.target.blur();
                                } else if (e.key === "Escape") {
                                    e.preventDefault();
                                    setRenamingSelf(false);
                                }
                            }}
                            className="min-w-0 flex-1 bg-[#0a0a0a] border border-[#1a3fd1]/60 rounded-sm px-1 py-0.5 text-sm text-white outline-none focus:ring-1 focus:ring-[#1a3fd1]/60"
                        />
                    ) : (
                        <span
                            className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap"
                            title={
                                isLockedSplitParent
                                    ? `${file.display_name || file.original_filename} · split — edit individual pages below`
                                    : `${file.display_name || file.original_filename} · press & hold to reorder`
                            }
                            onDoubleClick={(e) => {
                                if (isLockedSplitParent) return;
                                e.stopPropagation();
                                e.preventDefault();
                                setRenamingSelf(true);
                            }}
                        >
                            {file.display_name || file.original_filename}
                        </span>
                    )}
                    {file.pinned && !renamingSelf && (
                        <Pin className="w-3 h-3 shrink-0 text-[#8fa4f0] fill-current" />
                    )}
                    {isPdfFile && pageCount > 0 && !renamingSelf && (
                        <span className="shrink-0 text-[10px] text-slate-500 tabular-nums">
                            {pageCount}p
                        </span>
                    )}
                    {isLockedSplitParent && !renamingSelf && (
                        <span
                            className="shrink-0 text-[9px] uppercase tracking-wider font-bold text-slate-500"
                            title="This PDF has been split — edit the individual pages below instead"
                        >
                            split
                        </span>
                    )}
                </button>
            </div>
            {expanded && hasChildren && (
                <ul
                    className="mt-1 ml-10 mr-1 border-l border-white/15 space-y-0.5"
                    data-testid={`workspace-children-${file.id}`}
                >
                    {childRows.map((child) => {
                        const childActive = activeFileId === child.id;
                        const renamingThisChild = renamingChildId === child.id;
                        return (
                            <li
                                key={child.id}
                                className="group/childitem flex items-stretch gap-1 pl-2"
                                data-pinned={child.pinned ? "true" : "false"}
                            >
                                <button
                                    type="button"
                                    onClick={() => !renamingThisChild && onPickFile(child.id)}
                                    data-testid={`workspace-child-${child.id}`}
                                    className={`flex-1 min-w-0 flex items-center gap-2 pl-2 pr-2 py-1.5 text-xs text-left rounded-sm transition-colors ${
                                        childActive
                                            ? "bg-[#1a3fd1]/25 text-white"
                                            : "text-slate-400 hover:bg-white/10 hover:text-white"
                                    }`}
                                >
                                    <span className="tabular-nums text-[10px] text-slate-500 shrink-0">
                                        {String(child.page_number || 0).padStart(2, "0")}
                                    </span>
                                    {renamingThisChild ? (
                                        <input
                                            ref={renameInputRef}
                                            type="text"
                                            defaultValue={child.display_name || `Page ${child.page_number}`}
                                            placeholder={`Page ${child.page_number}`}
                                            data-testid={`workspace-rename-input-${child.id}`}
                                            onClick={(e) => e.stopPropagation()}
                                            onBlur={(e) => {
                                                const v = e.target.value;
                                                setRenamingChildId(null);
                                                if ((v || "").trim() !== (child.display_name || "").trim()) {
                                                    onRename && onRename(child, v);
                                                }
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    e.target.blur();
                                                } else if (e.key === "Escape") {
                                                    e.preventDefault();
                                                    setRenamingChildId(null);
                                                }
                                            }}
                                            className="min-w-0 flex-1 bg-[#0a0a0a] border border-[#1a3fd1]/60 rounded-sm px-1 py-0 text-xs text-white outline-none focus:ring-1 focus:ring-[#1a3fd1]/60"
                                        />
                                    ) : (
                                        <span
                                            className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap"
                                            title={`${child.display_name || `Page ${child.page_number}`} · double-click to rename`}
                                            onDoubleClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                setRenamingChildId(child.id);
                                            }}
                                        >
                                            {child.display_name || `Page ${child.page_number}`}
                                        </span>
                                    )}
                                    {child.pinned && !renamingThisChild && (
                                        <Pin className="w-3 h-3 shrink-0 text-[#8fa4f0] fill-current" />
                                    )}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
            {expanded && !hasChildren && (
                <ul
                    className="mt-1 ml-10 mr-1 border-l border-white/15 space-y-0.5"
                    data-testid={`workspace-subpages-${file.id}`}
                >
                    {Array.from({ length: pageCount }, (_, idx) => {
                        const p = idx + 1;
                        const pageActive = active && activePage === p;
                        return (
                            <li key={p} className="group/subpage">
                                <button
                                    type="button"
                                    onClick={() => onPickPage(p)}
                                    data-testid={`workspace-subpage-${file.id}-${p}`}
                                    className={`w-full flex items-center gap-2 pl-3 pr-2 py-1.5 text-xs text-left rounded-sm transition-colors ${
                                        pageActive
                                            ? "bg-[#1a3fd1]/20 text-white"
                                            : "text-slate-400 hover:bg-white/10 hover:text-white"
                                    }`}
                                >
                                    <span className="tabular-nums text-[10px] text-slate-500">
                                        {String(p).padStart(2, "0")}
                                    </span>
                                    <span>Page {p}</span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
            {/* AI-takeoff shapes nested under this document — Planswift
                "Pages, Bookmarks" style: type icon, name, right-aligned
                qty + unit, big colour swatch, visibility toggle. */}
            {takeoffItems && takeoffItems.length > 0 && (expanded || !canExpand) && (
                <ul
                    className="ml-5 mt-0.5 mb-1 space-y-[1px] border-l border-dotted border-slate-600/60 pl-2"
                    data-testid={`workspace-takeoffs-${file.id}`}
                >
                    {takeoffItems.slice(0, 40).map((it) => {
                        const meta = TAKEOFF_META[it.type] || {};
                        const Icon = meta.Icon || PenTool;
                        const selected = selectedTakeoffId === it.id;
                        const [qtyNum, qtyUnit] = splitTakeoffQty(it);
                        const isHidden = !!it.hidden;
                        return (
                            <li
                                key={it.id}
                                className={`group/takeoff flex items-center gap-1.5 px-1.5 py-[3px] rounded-sm text-[11px] cursor-pointer transition-colors ${
                                    selected
                                        ? "bg-[#1a3fd1]/15 text-white border-b border-[#1a3fd1]/50"
                                        : "text-slate-200 hover:bg-white/10"
                                } ${isHidden ? "opacity-50" : ""}`}
                                data-testid={`takeoff-item-${it.id}`}
                                onClick={() => {
                                    onSelectTakeoff && onSelectTakeoff(it.id);
                                    onJumpToTakeoff && onJumpToTakeoff(it);
                                }}
                            >
                                <Icon
                                    className={`w-3.5 h-3.5 shrink-0 ${meta.tone || "text-slate-400"}`}
                                    aria-hidden
                                />
                                <span
                                    className={`flex-1 min-w-0 truncate ${
                                        selected ? "text-[#8fa4f0] font-semibold" : "text-slate-200"
                                    }`}
                                >
                                    {it.name || meta.label || "Shape"}
                                </span>
                                <span className="shrink-0 text-[11px] font-semibold text-[#8fa4f0] tabular-nums">
                                    {qtyNum}
                                </span>
                                {qtyUnit && (
                                    <span className="shrink-0 text-[9px] uppercase tracking-wider text-slate-400 font-bold">
                                        {qtyUnit}
                                    </span>
                                )}
                                <span
                                    aria-hidden
                                    className="w-5 h-3.5 shrink-0 rounded-[2px] border border-black/40 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]"
                                    style={{ backgroundColor: it.color || "#64748b" }}
                                    title={`Colour: ${it.color || "default"}`}
                                />
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onToggleTakeoffVisibility &&
                                            onToggleTakeoffVisibility(it);
                                    }}
                                    data-testid={`takeoff-visibility-${it.id}`}
                                    aria-label={
                                        isHidden ? "Show this shape" : "Hide this shape"
                                    }
                                    title={
                                        isHidden
                                            ? "Hidden — click to show"
                                            : "Visible — click to hide"
                                    }
                                    className="shrink-0 p-0.5 rounded hover:bg-amber-400/15 text-slate-500 transition-colors"
                                >
                                    <Lightbulb
                                        className={`w-3.5 h-3.5 ${
                                            isHidden
                                                ? "text-slate-600"
                                                : "text-amber-400 fill-amber-400/80"
                                        }`}
                                    />
                                </button>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDeleteTakeoff && onDeleteTakeoff(it.id);
                                    }}
                                    data-testid={`takeoff-delete-${it.id}`}
                                    aria-label="Delete takeoff shape"
                                    className="opacity-0 group-hover/takeoff:opacity-100 p-0.5 rounded hover:bg-rose-500/20 text-slate-500 hover:text-rose-400 transition-all"
                                >
                                    <Trash2 className="w-3 h-3" />
                                </button>
                            </li>
                        );
                    })}
                    {takeoffItems.length > 40 && (
                        <li className="px-1.5 py-0.5 text-[10px] text-slate-500">
                            + {takeoffItems.length - 40} more shapes…
                        </li>
                    )}
                </ul>
            )}
        </li>
    );
}
